import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { type Move } from '../../../modules/moves'
import { type Quote } from '../../../modules/quotes'
import QuoteComponent from '../QuoteComponent'
import { type QuoteComponentOptions } from '../QuoteComponent.types'
import QuotesViewsDropdown from './QuotesViewsDropdown'
import { useSaveQuotesViewAction, useDefaultQuotesView, useSortedQuote } from './quotesView.hooks'
import { type QuotesView } from './quotesView.types'
import { mergeClassName } from '../../../utils/mergeClassName'

type AvailableOptions = Pick<QuoteComponentOptions, 'docTop' | 'onAlternativeDateClick'>

type QuotesProps = {
  move: Move
  cta?: (quote: Quote, position: number) => React.ReactNode
  customerPerspective?: boolean
  showBest?: boolean
  topSection?: React.ReactNode
  saveSorting?: boolean
} & AvailableOptions

const Quotes: React.FC<QuotesProps> = ({
  move,
  cta,
  customerPerspective = false,
  showBest = false,
  topSection,
  saveSorting = true,
  ...quoteOptions
}) => {
  const { t } = useTranslation()
  const defaultQuotesView = useDefaultQuotesView(move)
  const saveQuotesView = useSaveQuotesViewAction()
  const [quotesView, setQuotesView] = useState<QuotesView>(defaultQuotesView)
  const promoCode = move.promoCode.appliedPromoCode ?? move.promoCode.pendingPromoCode
  const hasQuotes = !!move.quotes?.length

  const customerDistanceAndDuration = move.jobs.map(job => ({
    distanceMeters: job.resolved?.transportDistanceMeters,
    durationSeconds: job.resolved?.jobDurationSeconds,
  }))

  const quotes = move.quotes ?? []
  const sortedQuotes = useSortedQuote(quotes, quotesView)

  const handleChangeView = (view: QuotesView) => {
    if (saveSorting) {
      saveQuotesView(view)
    }
    setQuotesView(view)
  }

  return (
    <>
      <div className="mb-4 flex flex-col-reverse items-center justify-between gap-y-4 lg:flex-row">
        <div className={mergeClassName(
          'flex justify-end',
          !topSection && 'flex-grow',
        )}
        >
          { hasQuotes && (
            <QuotesViewsDropdown
              quotesView={quotesView}
              setQuotesView={handleChangeView}
            />
          ) }
        </div>
        { topSection }
      </div>
      <div
        key={quotesView}
        className="flex flex-col gap-y-8"
      >
        { sortedQuotes.map((quote, index) => {
          const selected = !!move.selectedQuote?.id && move.selectedQuote.id === quote.id
          const best = !selected && showBest && index === 0

          const highlightedText = (() => {
            if (selected) {
              return t('quotes.quote.selected')
            }
            if (showBest && best) {
              return t('quotes.quote.best')
            }
          })()

          return (
            <div key={quote.id}>
              <QuoteComponent
                quote={quote}
                options={{
                  promoCode,
                  cta: cta?.(quote, index + 1),
                  highlightedText,
                  openByDefault: selected || (best && showBest),
                  customerDistanceAndDuration: customerPerspective ? customerDistanceAndDuration : undefined,
                  ...quoteOptions,
                }}
              />
            </div>
          )
        }) }

        { sortedQuotes.length === 0 && (
          <div>
            { t('quotes.noQuotes') }
          </div>
        ) }
      </div>
    </>
  )
}

export default Quotes
